// Progress Bar Component
// Shows checkout progress with step indicators

import { clsx } from 'clsx';
import type { CheckoutStep } from '../store/checkout';
import { useCheckoutStore } from '../store/checkout';
import { getMarketConfig } from '../config/market';
import styles from './ProgressBar.module.css';

function useStepLabels() {
  const locale = useCheckoutStore((s) => s.locale);
  const L = getMarketConfig(locale).labels;
  return [
    { key: 'identity' as CheckoutStep, label: L.stepContact },
    { key: 'address' as CheckoutStep, label: L.stepAddress },
    { key: 'shipping' as CheckoutStep, label: L.stepShipping },
    { key: 'payment' as CheckoutStep, label: L.stepPayment },
  ];
}

interface ProgressBarProps {
  progress: number;
  currentStep: CheckoutStep;
  onStepClick?: (step: CheckoutStep) => void;
}

export function ProgressBar({ progress, currentStep, onStepClick }: ProgressBarProps) {
  const STEPS = useStepLabels();
  const locale = useCheckoutStore((s) => s.locale);
  const L = getMarketConfig(locale).labels;
  const currentIndex = STEPS.findIndex((s) => s.key === currentStep);
  
  return (
    <div className={styles.container}>
      {/* Progress bar */}
      <div className={styles.bar}>
        <div 
          className={clsx(styles.fill, 'progress-animate')} 
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {/* Step indicators */}
      <div className={styles.steps}>
        {STEPS.map((step, index) => {
          const isCompleted = index < currentIndex;
          return (
            <div 
              key={step.key}
              className={clsx(styles.step, {
                [styles.completed]: isCompleted,
                [styles.active]: index === currentIndex,
                [styles.upcoming]: index > currentIndex,
                [styles.clickable]: isCompleted && !!onStepClick,
              })}
              onClick={isCompleted && onStepClick ? () => onStepClick(step.key) : undefined}
              role={isCompleted && onStepClick ? 'button' : undefined}
              tabIndex={isCompleted && onStepClick ? 0 : undefined}
              onKeyDown={isCompleted && onStepClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(step.key);
                }
              } : undefined}
              aria-label={isCompleted && onStepClick ? `${L.goBackToStep} ${step.label}` : undefined}
            >
              <div className={styles.dot}>
                {isCompleted ? (
                  <CheckIcon />
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <span className={styles.label}>{step.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path 
        d="M10 3L4.5 8.5L2 6" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );
}
